import React, { useMemo, useRef, useState } from "react";
import { showError, showSuccess } from "toast/toast";
import SectionHeader from "components/SectionPillPropsIcons";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles } from "components/Imageshelper";
import { getAuthCookie } from "../utils/authCookies";
export type ProofFile = {
  file_name: string;
  file_url: string;
  content_type?: string;
  uploaded_date?: string;
};

// type Props = {
//   title: string;                         // "Proof of Installation" | "Proof of Removal"
//   canEdit: boolean;                      // gate by status
//   existing: ProofFile[];                 // already uploaded on the order
//   setExisting: (files: ProofFile[]) => void; // lift state to the page
//   uploadFile: (file: File) => Promise<string>; // your existing uploader → returns URL
//   onSubmitProof: (newFiles: ProofFile[]) => Promise<void>; // call your proof API
//   accept?: string;                       // default: images only
//   maxSizeMB?: number;                    // default: 10
//   onDeleteExisting?: (file: ProofFile, index: number) => Promise<void> | void;
// };

type Props = {
  title: string;                                      // "Proof of Installation" | "Proof of Removal"
  canEdit: boolean;                                   // gate by status
  existing: ProofFile[];                              // already persisted on order
  setExisting: React.Dispatch<React.SetStateAction<ProofFile[]>>;  // lift state to page
  onSubmitProof: (newFiles: ProofFile[]) => Promise<void>;         // persist NEW uploads on API
  onRegisterUploadHandler?: (uploadFn: () => Promise<void>) => void;
  showNotifyCustomer?: boolean;
  onNotifyCustomer?: () => Promise<void>;
  accept?: string;                                    // default: image types
  maxSizeMB?: number;                                 // default: 10
};
export default function ProofSection({
  // title,
  // canEdit,
  // existing,
  // setExisting,
  // uploadFile,
  // onSubmitProof,
  // accept = "image/png,image/jpeg,image/gif",
  // maxSizeMB = 10,
  title,
  canEdit,
  existing,
  setExisting,
  onSubmitProof,
  onRegisterUploadHandler,
  showNotifyCustomer = false,
  onNotifyCustomer,
  accept = "image/png,image/jpeg,image/gif",
  maxSizeMB = 10,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previews, setPreviews] = useState<{ file: File; url: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const token = getAuthCookie("sessionToken") || null;
  const uploadedIds = useMemo(() => {
    const ids: string[] = [];
    (existing || []).forEach((f) => {
      const id = String(f.file_url || "");
      if (/^[a-f0-9]{24}$/i.test(id)) ids.push(id);
    });
    return Array.from(new Set(ids));
  }, [existing]);

  const presignPayload = useMemo(
    () =>
      uploadedIds.length
        ? [{ product: { variants: [{ images: uploadedIds.map((id) => ({ url: id })) }] } }]
        : [],
    [uploadedIds]
  );

  const imageUrlMap = useVariantImageUrlMap(presignPayload, token);

  // ---- pick files → local previews (not uploaded yet) ----
  const handlePick = (files: FileList | null) => {
    if (!files) return;
    const next: { file: File; url: string }[] = [];
    const types = (accept || "").split(",").map((t) => t.trim()).filter(Boolean);

    Array.from(files).forEach((f) => {
      const okType = types.length
        ? types.some((t) => (t.startsWith("image/") ? f.type === t : f.type.includes(t)))
        : true;
      if (!okType) return;
      if (f.size > (maxSizeMB || 10) * 1024 * 1024) {
        showError(`"${f.name}" exceeds ${maxSizeMB}MB`);
        return;
      }
      next.push({ file: f, url: URL.createObjectURL(f) });
    });

    if (next.length) setPreviews((prev) => [...prev, ...next]);
    if (inputRef.current) inputRef.current.value = ""; // allow re-pick
  };

  // ---- upload previews → Cloud Drive → persist NEW on API → merge into existing ----
  const uploadAll = async () => {
    if (!previews.length) return;
    try {
      setBusy(true);

      // 1) Upload all preview files to Cloud Drive
      const ids = await uploadFilesAndGetIds(previews.map((p) => p.file), token);

      // 2) Build ProofFile[] for NEW files only
      const uploaded: ProofFile[] = previews.map((p, i) => ({
        file_name: p.file.name,
        file_url: ids[i], // store cloud id
        content_type: p.file.type,
        uploaded_date: new Date().toISOString(),
      }));

      // 3) Persist NEW files to your API
      await onSubmitProof(uploaded);

      // 4) Merge into existing (de-dupe by file_name), then clear previews
      setExisting((prev) => {
        const byName = new Map<string, ProofFile>();
        [...(prev || []), ...uploaded].forEach((f) => byName.set(f.file_name, f));
        return Array.from(byName.values());
      });

      previews.forEach((p) => URL.revokeObjectURL(p.url));
      setPreviews([]);
      showSuccess("Files uploaded successfully!");
    } catch (e: any) {
      showError(e?.message || "Upload failed");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => {
    if (!onRegisterUploadHandler) return;
    onRegisterUploadHandler(uploadAll);
  }, [onRegisterUploadHandler, uploadAll]);

  // ---- remove a single preview (not uploaded to server yet) ----
  const removePreview = (i: number) => {
    setPreviews((prev) => {
      URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  // ---- remove ONE uploaded image (hard delete from Cloud + update UI) ----
  const removeUploadedOne = async (file: ProofFile, index: number) => {
    try {
      const maybeId = String(file.file_url || "");
      if (/^[a-f0-9]{24}$/i.test(maybeId)) {
        await hardDeletePresignedFiles([maybeId], token);
      }
      setExisting((prev) => prev.filter((_, i) => i !== index));
      showSuccess("Image removed");
    } catch (e: any) {
      showError(e?.message || "Delete failed");
    }
  };

  const totalUploaded = existing?.length ?? 0;
  const uploadedLabel = useMemo(() => `Uploaded Images (${totalUploaded})`, [totalUploaded]);
  const hasAtLeastOneProofFile = totalUploaded + previews.length > 0;

  const handleUploadAndNotify = async () => {
    if (!hasAtLeastOneProofFile || notifyBusy) return;
    setNotifyBusy(true);
    try {
      if (previews.length) {
        await uploadAll();
      } else {
        await onSubmitProof(existing || []);
      }
    } catch {
      setNotifyBusy(false);
      return;
    }

    try {
      if (onNotifyCustomer) await onNotifyCustomer();
    } catch (e: any) {
      showError(e?.message || "Failed to notify customer");
    } finally {
      setNotifyBusy(false);
    }
  };



  // const handlePick = (files: FileList | null) => {
  //   if (!files) return;
  //   const next: { file: File; url: string }[] = [];
  //   Array.from(files).forEach((f) => {
  //     if (!accept.split(",").some(t => f.type.includes(t.replace("image/", "")))) return;
  //     if (f.size > maxSizeMB * 1024 * 1024) {
  //       showError(`"${f.name}" exceeds ${maxSizeMB}MB`);
  //       return;
  //     }
  //     next.push({ file: f, url: URL.createObjectURL(f) });
  //   });
  //   setPreviews(prev => [...prev, ...next]);
  //   if (inputRef.current) inputRef.current.value = ""; // allow same file re-pick
  // };

  // const uniqueByName = (items: ProofFile[]) =>
  //   items.filter((f, i, self) => i === self.findIndex(x => x.file_name === f.file_name));

  // const uploadAll = async () => {
  //   if (!previews.length) return;
  //   try {
  //     setBusy(true);
  //     // 1) upload to storage
  //     const uploaded: ProofFile[] = [];
  //     for (const p of previews) {
  //       const url = await uploadFile(p.file);
  //       uploaded.push({
  //         file_name: p.file.name,
  //         file_url: url,
  //         content_type: p.file.type,
  //         uploaded_date: new Date().toISOString(),
  //       });
  //     }
  //     // 2) call proof API with NEW files only
  //     await onSubmitProof(uploaded);
  //     // 3) merge into existing list (de-dupe by name) and clear previews
  //     setExisting(uniqueByName([...(existing || []), ...uploaded]));
  //     setPreviews([]);
  //     showSuccess("Files uploaded successfully!");
  //   } catch (e: any) {
  //     showError(e?.message || "Upload failed");
  //   } finally {
  //     setBusy(false);
  //   }
  // };

  // const removePreview = (i: number) => {
  //   setPreviews(prev => {
  //     URL.revokeObjectURL(prev[i].url);
  //     return prev.filter((_, idx) => idx !== i);
  //   });
  // };

  // const uploadedLabel = useMemo(
  //   () => `Uploaded Images (${totalUploaded})`,
  //   [totalUploaded]
  // );
  // remove saved (uploadedFileMeta) by index and queue cloud id for hard delete
  
  return (
    <div className="border-0 card shadow-none box-shadow shadow-border-3 me-lg-2 me-0">
      <div className="card-body">
        <div className="mb-3 d-flex align-items-start justify-content-between gap-3 flex-wrap">
          <div className="flex-grow-1">
            <SectionHeader title={title} icon="ri-image-2-line" color="#4f39f6" size="lg" divider />
          </div>
          {showNotifyCustomer && (
            <button
              type="button"
              className="btn btn-primary table-addbutton flex-shrink-0"
              disabled={!hasAtLeastOneProofFile || notifyBusy || busy}
              onClick={() => void handleUploadAndNotify()}
            >
              {notifyBusy ? "Uploading..." : "Upload"}
            </button>
          )}
        </div>
        {/* Upload area */}
        <div
          className={
            "dropzone border border-2 border-dashed rounded-4 p-5 text-center " +
            "bg-body-tertiary " +                             // light background
            (canEdit ? "editable" : "d-none")
          }
          style={{ borderStyle: "dashed", minHeight: 140, cursor: canEdit ? "pointer" : "default" }}
          onClick={() => canEdit && inputRef.current?.click()}
          onDragOver={(e) => { if (canEdit) { e.preventDefault(); e.stopPropagation(); } }}
          onDrop={(e) => {
            if (!canEdit) return;
            e.preventDefault();
            handlePick(e.dataTransfer.files);
          }}
        >
          {/* <div>
            <div className="mb-2">
              <i className="ri-upload-2-line fs-1 text-secondary mb-2" />   
            </div>
            <div className="text-decoration-none p-0">
              Click to upload images
            </div>
            <div className="text-muted small">PNG, JPG, GIF up to {maxSizeMB}MB each</div>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={accept}
              className="d-none"
              onChange={(e) => handlePick(e.target.files)}
              disabled={!canEdit}
            />
          </div> */}
          <div>
            <div className="mb-2">
              <i className="ri-upload-2-line fs-1 text-secondary mb-2" />
            </div>
            <div className="text-decoration-none p-0">Click to upload images</div>
            <div className="text-muted small">PNG, JPG, GIF up to {maxSizeMB}MB each</div>
            {/* <input
              ref={inputRef}
              type="file"
              multiple
              accept={accept}
              className="d-none"
              onChange={(e) => handlePick(e.target.files)}
              disabled={!canEdit}
            /> */}
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={accept}
              className="d-none"
              onChange={(e) => handlePick(e.target.files)}
              disabled={!canEdit}
            />
          </div>
        </div>

        {previews.length > 0 && (
          <>
            <div className="d-flex justify-content-between align-items-center my-3">
              <div className="fw-semibold">Selected</div>
            </div>
            <div className="row g-3 mb-3">
              {previews.map((p, i) => (
                <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={i}>
                  <div className="border rounded-3 p-2 h-100 thumb position-relative">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm rounded-circle p-0 d-inline-flex align-items-center justify-content-center thumb-remove position-absolute"
                      title="Remove"
                      onClick={() => removePreview(i)}
                      style={{ width: 24, height: 24, right: 6, top: 6 }}
                    >
                      <i className="ri-close-line" />
                    </button>

                    <div className="ratio ratio-1x1 rounded-3 overflow-hidden mb-2">
                      <img src={p.url} alt={p.file.name} className="w-100 h-100 object-fit-cover" />
                    </div>
                    <div className="small text-truncate" title={p.file.name}>
                      {p.file.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Uploaded gallery */}
        {existing?.length > 0 ? (
          <>
            <div className="fw-semibold mb-2">Uploaded Images ({existing.length})</div>
            <div className="row g-3">
              {existing.map((file, i) => {
                const idOrUrl = String(file.file_url || "");
                const src =
                  getImageSrc(idOrUrl, imageUrlMap) ||
                  (idOrUrl.startsWith("http")
                    ? idOrUrl
                    : `${process.env.REACT_APP_BASE_URL}/${idOrUrl.replace(
                      /^https?:\/\/[^/]+\/(uploads\/.*)/,
                      "$1"
                    )}`);

                return (
                  <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={`${idOrUrl}-${i}`}>
                    <div className="border rounded-3 p-2 h-100 thumb position-relative">
                      {canEdit && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm rounded-circle p-0 d-inline-flex align-items-center justify-content-center thumb-remove position-absolute"
                          title="Remove"
                          onClick={() => removeUploadedOne(file, i)}
                          style={{ width: 22, height: 22, right: 6, top: 6 }}
                        >
                          <i className="ri-close-line" />
                        </button>
                      )}

                      <a href={src || "#"} target="_blank" rel="noreferrer" className="text-decoration-none">
                        <div className="ratio ratio-1x1 rounded-3 overflow-hidden mb-2 bg-light">
                          {src ? (
                            <img src={src} alt={file.file_name} className="w-100 h-100 object-fit-cover" />
                          ) : (
                            <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted small">
                              Loading…
                            </div>
                          )}
                        </div>
                        <div className="small text-truncate" title={file.file_name}>
                          {file.file_name}
                        </div>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center">No {title} images uploaded</div>
        )}

      </div>
    </div>
  );
}
